import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import routes from 'router/routes';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { Switch as EmSwitch } from 'components/Switch';
import { tCategory } from 'services/Ticketing/Ticketing.type';

export const CATEGORY_LABELS: Record<string, string> = {
  visitor: 'Visiteur',
  exhibitor: 'Exposant',
  setup: 'Montage / démontage',
  organiser: 'Organisateur',
};

export const CATEGORY_COLORS: Record<string, { bg: string; fg: string }> = {
  visitor: { bg: '#f0fdfa', fg: '#115e59' },
  exhibitor: { bg: '#fffbeb', fg: '#92400e' },
  setup: { bg: '#f1f5f9', fg: '#334155' },
  organiser: { bg: '#fff1f2', fg: '#9f1239' },
};

export function CategoryChip({
  category,
}: {
  category: tCategory | string | null;
}): JSX.Element | null {
  if (!category) return null;
  const c = CATEGORY_COLORS[category] ?? CATEGORY_COLORS.visitor;
  return (
    <Chip
      size="small"
      label={CATEGORY_LABELS[category] ?? category}
      sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 600, height: 22 }}
    />
  );
}

export function formatMoney(
  cents: number | null | undefined,
  currency = 'EUR',
  locale = 'fr-FR'
): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
    (cents ?? 0) / 100
  );
}

export function formatDay(
  value: string | null | undefined,
  locale = 'fr-FR'
): string {
  if (!value) return '';
  const d = new Date(`${value.slice(0, 10)}T12:00:00`);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}

export function formatShortDay(value: string, locale = 'fr-FR'): string {
  const d = new Date(`${value.slice(0, 10)}T12:00:00`);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Opens a downloaded file (PDF preview, CSV export). */
export function openBlob(blob: Blob, filename?: string): void {
  const url = URL.createObjectURL(blob);
  if (filename) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  } else {
    window.open(url, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Page frame shared with the other EM pages: centred, 1200 px wide. */
export const PAGE_SX = {
  maxWidth: 1200,
  mx: 'auto',
  px: { xs: 2, md: 4 },
  py: 4,
  // MUI components default to Roboto: use the EM font instead
  [`& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root,
    & .MuiInputLabel-root, & .MuiTableCell-root, & .MuiChip-root,
    & .MuiFormControlLabel-label, & .MuiAlert-message, & .MuiMenuItem-root`]: {
    fontFamily: 'inherit',
  },
} as const;

/** Black primary button, as on the EM event list. */
export const primaryButton = {
  bgcolor: '#000',
  color: '#fff',
  fontWeight: 600,
  fontSize: '13px',
  textTransform: 'none',
  borderRadius: '8px',
  px: 2,
  py: 1,
  boxShadow: 'none',
  '&:hover': { bgcolor: '#222', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#e5e5e5', color: '#999' },
} as const;

/** Outlined secondary button. */
export const secondaryButton = {
  color: '#111',
  borderColor: '#ddd',
  bgcolor: '#fff',
  fontWeight: 600,
  fontSize: '13px',
  textTransform: 'none',
  borderRadius: '8px',
  px: 2,
  py: 1,
  '&:hover': { borderColor: '#111', bgcolor: '#fff' },
} as const;

/** Title of a card or section. */
export const sectionTitle = {
  fontSize: '15px',
  fontWeight: 700,
  color: '#111',
} as const;

/** Header of the ticketing pages (title, event, scanner) and their tabs. */
export function TicketingNav({
  current,
  canConfigure,
  tickets,
  eventName,
}: {
  current: 'setup' | 'tickets' | 'exhibitors' | 'checkpoints' | 'stats';
  canConfigure: boolean;
  tickets?: number;
  eventName?: string;
}): JSX.Element {
  const { eventId } = useParams();
  const navigate = useNavigate();
  useLocation();
  const base = `${routes.home2.path}events/${eventId ?? ''}/ticketing`;
  const tabs = [
    ...(canConfigure
      ? [{ key: 'setup', label: 'Configuration', to: base }]
      : []),
    {
      key: 'tickets',
      label: `Billets${tickets !== undefined ? ` (${tickets})` : ''}`,
      to: `${base}/tickets`,
    },
    ...(canConfigure
      ? [
          {
            key: 'exhibitors',
            label: 'Codes exposants',
            to: `${base}/exhibitors`,
          },
        ]
      : []),
    {
      key: 'checkpoints',
      label: 'Points de contrôle',
      to: `${base}/checkpoints`,
    },
    { key: 'stats', label: 'Données visiteurs', to: `${base}/stats` },
  ];
  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2.5,
        }}
      >
        <Box>
          <Box sx={{ fontSize: '22px', fontWeight: 700, color: '#111' }}>
            Billetterie
          </Box>
          {eventName && (
            <Box sx={{ fontSize: '13px', color: '#888', mt: 0.5 }}>
              {eventName}
            </Box>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<QrCodeScannerIcon />}
          onClick={() => navigate(`${routes.home2.path}scan/${eventId ?? ''}`)}
          sx={primaryButton}
        >
          Ouvrir le scanner
        </Button>
      </Box>
      <Box
        component="nav"
        sx={{
          display: 'flex',
          gap: 3,
          borderBottom: '1px solid #e5e5e5',
          overflowX: 'auto',
        }}
      >
        {tabs.map((t) => {
          const active = t.key === current;
          return (
            <Box
              key={t.key}
              component="button"
              type="button"
              onClick={() => navigate(t.to)}
              aria-current={active ? 'page' : undefined}
              sx={{
                background: 'none',
                border: 0,
                borderBottom: '2px solid',
                borderColor: active ? '#111' : 'transparent',
                mb: '-1px',
                px: 0,
                py: 1.25,
                fontSize: '13px',
                fontWeight: active ? 600 : 500,
                color: active ? '#111' : '#777',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontFamily: 'inherit',
                '&:hover': { color: '#111' },
              }}
            >
              {t.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export const card = {
  bgcolor: '#fff',
  border: '1px solid #eee',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  p: { xs: 2, md: 3 },
} as const;

/**
 * EM's OUI / NON switch (as in the exhibitors list), with its label. A
 * rejected onChange puts the switch back.
 */
export function SwitchField({
  label,
  checked,
  onChange,
  disabled = false,
  hint,
}: {
  label: React.ReactNode;
  checked: boolean;
  onChange: (value: boolean) => unknown;
  disabled?: boolean;
  hint?: React.ReactNode;
}): JSX.Element {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <EmSwitch
        checked={checked}
        disable={disabled}
        onChange={async (value) => {
          await onChange(value);
          return true;
        }}
      />
      <Box>
        <Typography sx={{ fontSize: '13px', color: '#111' }}>
          {label}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: '12px', color: '#666' }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
